import { directoryApi } from "../../api/directoryApi";
import { getGraphTokenProvider, preparePhoto, readMe } from "../../auth/graph";

// A. The signed-in person's own Microsoft details and photo (O-R56, owner 2026-10-22): read in THEIR browser with THEIR
// delegated User.Read token after sign-in, at most once a day per person and device, and stored by the API against their
// own sign-in only. Silent and non-blocking: no consent, no photo, offline — nothing is shown and nothing breaks.

export const SELF_SYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;
const KEY_PREFIX = "summitops.directory.self-sync.";

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** True when this device has not refreshed this person in the last 24 hours. */
export function selfSyncDue(upn: string, now = Date.now()): boolean {
  const last = Number(storage()?.getItem(KEY_PREFIX + upn.toLowerCase()) ?? "0");
  return !Number.isFinite(last) || last <= 0 || last > now || now - last >= SELF_SYNC_INTERVAL_MS;
}

export function markSelfSynced(upn: string, now = Date.now()): void {
  try {
    storage()?.setItem(KEY_PREFIX + upn.toLowerCase(), String(now));
  } catch {
    /* private mode: the server's own 24 h limit still applies */
  }
}

let running = false;

/** Runs the silent self-sync once when due. Resolves true when the API stored something. Never throws. */
export async function runSelfSync(upn: string | null | undefined, fetcher: typeof fetch = fetch): Promise<boolean> {
  if (!upn || running || !selfSyncDue(upn)) return false;
  const provider = getGraphTokenProvider();
  if (!provider) return false;
  running = true;
  try {
    const { token } = await provider("userRead", "silent");
    if (!token) return false;
    const { me, photo, photoState } = await readMe(token, fetcher);
    const prepared = await preparePhoto(photo, photoState);
    const result = await directoryApi.syncMine({
      id: me.id ?? null,
      userPrincipalName: me.userPrincipalName ?? null,
      displayName: me.displayName ?? null,
      givenName: me.givenName ?? null,
      surname: me.surname ?? null,
      jobTitle: me.jobTitle ?? null,
      department: me.department ?? null,
      employeeId: me.employeeId ?? null,
      mail: me.mail ?? null,
      managerDisplayName: null,
      managerUpn: null,
      ...prepared
    });
    markSelfSynced(upn);
    return result.applied;
  } catch {
    return false;
  } finally {
    running = false;
  }
}
